/**
 * MER-532: the tenant-context the app reads when resolving where a
 * multi-membership user lands. Two slots, both keyed by the Supabase user id so
 * the same browser logged in as different users never crosses choices (same
 * convention as lib/userPreferences):
 *
 *   - ACTIVE workspace (sessionStorage): the workspace the "Choose your
 *     workspace" picker set for THIS browser session. Cleared when the tab's
 *     session ends, so each new session re-resolves (and re-prompts if there's
 *     still no default). This is what the picker writes.
 *
 *   - DEFAULT workspace (localStorage): an explicit, durable default_tenant.
 *     resolveWorkspace HONORS it when present, but the WRITER (a settings /
 *     admin surface, or "remember my choice" persistence) is the deferred
 *     follow-up that lands after MER-528. Read-only here on purpose; until a
 *     future surface sets it, this returns null and the picker drives routing.
 *
 * All access is wrapped so a disabled/over-quota storage (private mode, etc.)
 * degrades to "no stored choice" rather than throwing into the render tree.
 */

const ACTIVE_PREFIX = "meridian:active-workspace:";
const DEFAULT_PREFIX = "meridian:default-workspace:";

function userKey(prefix: string, userId: string | null): string {
  return `${prefix}${userId ?? "anon"}`;
}

/** The workspace slug the picker set for this session, or null. */
export function getActiveWorkspace(userId: string | null): string | null {
  try {
    return window.sessionStorage.getItem(userKey(ACTIVE_PREFIX, userId));
  } catch {
    return null;
  }
}

/** Record the picked workspace for this session so the choice sticks. */
export function setActiveWorkspace(userId: string | null, slug: string): void {
  try {
    window.sessionStorage.setItem(userKey(ACTIVE_PREFIX, userId), slug);
  } catch {
    // sessionStorage unavailable / quota: routing falls back to the picker,
    // which is correct (just not sticky for the session).
  }
}

/**
 * An explicit durable default workspace (default_tenant), or null. Read-only:
 * the durable writer is the deferred post-MER-528 follow-up (see file header).
 */
export function getDefaultWorkspace(userId: string | null): string | null {
  try {
    return window.localStorage.getItem(userKey(DEFAULT_PREFIX, userId));
  } catch {
    return null;
  }
}
